'use client';

import { trackLinkClick } from 'utils/tracking';

import YouTubeEmbed from '@/components/behindTheSceens/YouTubeEmbed';
import ButtonLink, { ButtonLinkType } from '@/components/ui/ButtonLink';
import CDNImage from '@/components/ui/CDNImage';
import CloseIcon from '@/components/ui/CloseIcon';
import Modal from '@/components/ui/Modal';
import PlayVideoIcon from '@/components/ui/PlayVideoIcon';
import VideoClip from '@/components/ui/VideoClip';
import { Page_Pagebuilder_Blocks_TextMedia } from '@/graphql/generated/graphql';
import { m } from 'motion/react';
import { useState } from 'react';
import { containerVariants, viewPort } from 'utils/framer';
import { getContainerClass, getYoutubeId } from 'utils/helpers';
import Wysiwyg from '../components/dom-parser';

const TextAndMedia = ({
  blockId,
  containerWidth,
  mediaType,
  image,
  imageMobile,
  video,
  videoMobile,
  youtube,
  mediaIsCover,
  videoHasControls,
  videoHasAutoplay,
  mediaPosition,
  link,
  linkText,
  linkInNewWindow,
  linkType,
  linkPosition,
  contentBg,
  content,
}: Page_Pagebuilder_Blocks_TextMedia) => {
  const [currentVideoPlaying, setCurrentVideoPlaying] = useState<{
    mediaItemUrl: string;
    mimeType: string;
  } | null>(null);
  const isImage = Boolean(mediaType === 'image' && image?.sourceUrl);
  const isVideo = Boolean(mediaType === 'video' && video?.mediaItemUrl);
  const isYoutube = Boolean(mediaType === 'youtube' && youtube);
  const youtubeId = isYoutube ? getYoutubeId(youtube) : null;
  const mediaIsRight = Boolean(mediaPosition === 'right');

  return (
    <section id={blockId ?? undefined} className="w-full my-14 lg:my-24">
      <m.div
        initial="hidden"
        whileInView="visible"
        viewport={viewPort}
        variants={containerVariants}
        className={`w-full ${getContainerClass(containerWidth)}`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-center gap-4 lg:gap-0">
          <div
            className={`relative w-full h-full ${isImage && mediaIsCover ? 'aspect-[3/2]' : ''} ${mediaIsRight ? 'order-1 lg:order-2' : 'order-1'}`}
          >
            {isYoutube && youtubeId ? (
              <YouTubeEmbed videoId={youtubeId} />
            ) : null}
            {isImage ? (
              <div className="w-full h-full">
                <CDNImage
                  src={image.sourceUrl || ''}
                  alt={image.altText || ''}
                  width={image.mediaDetails.width || 0}
                  height={image.mediaDetails.height || 0}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  srcSet={image.srcSet || ''}
                  className={`w-full ${mediaIsCover ? 'object-cover h-full' : 'object-contain'} ${imageMobile ? 'hidden lg:block' : 'block'}`}
                  loading="eager"
                />
                {imageMobile && imageMobile.sourceUrl ? (
                  <CDNImage
                    src={imageMobile.sourceUrl || ''}
                    alt={imageMobile.altText || ''}
                    width={imageMobile.mediaDetails.width || 0}
                    height={imageMobile.mediaDetails.height || 0}
                    sizes="100vw"
                    srcSet={imageMobile.srcSet || ''}
                    className="block h-full w-full object-cover lg:hidden"
                    loading="eager"
                  />
                ) : null}
              </div>
            ) : null}
            {isVideo ? (
              <div
                className="w-full h-full"
                onClick={() =>
                  setCurrentVideoPlaying({
                    mediaItemUrl: video.mediaItemUrl,
                    mimeType: video.mimeType,
                  })
                }
              >
                {!videoHasAutoplay && (
                  <div className="absolute flex items-center justify-center w-full h-full top-0 left-0 cursor-pointer z-10">
                    <PlayVideoIcon className="w-16 h-16 text-white" />
                  </div>
                )}
                <video
                  autoPlay={videoHasAutoplay}
                  controls={videoHasControls}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className={`h-full w-full aspect-video object-cover ${videoMobile && videoMobile.mediaItemUrl ? 'hidden lg:block' : 'block'}`}
                >
                  <source
                    src={`${video.mediaItemUrl}#t=0.001`}
                    type={video.mimeType}
                  />
                </video>
                {videoMobile && videoMobile.mediaItemUrl ? (
                  <video
                    autoPlay={videoHasAutoplay}
                    controls={videoHasControls}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="block h-full w-full aspect-video object-cover lg:hidden"
                  >
                    <source
                      src={`${videoMobile.mediaItemUrl}#t=0.001`}
                      type={videoMobile.mimeType}
                    />
                  </video>
                ) : null}
              </div>
            ) : null}
          </div>
          {content && (
            <div
              className={`editor-card flex flex-col h-full px-4 md:px-0 lg:px-12 lg:justify-center order-2 lg:order-1 ${mediaIsRight ? 'order-2 lg:order-1' : 'order-2'} ${contentBg ? 'p-4 lg:pl-8 lg:pr-4 lg:pt-6 lg:pb-4' : ''}`}
              style={{
                backgroundColor: contentBg ? contentBg : 'transparent',
              }}
            >
              <Wysiwyg>{content}</Wysiwyg>
              {link && linkText && (
                <div
                  className={`${linkPosition === 'center' ? 'mx-auto' : linkPosition === 'right' ? 'max-lg:mx-auto lg:ml-auto' : 'max-lg:mx-auto lg:mr-auto'}`}
                >
                  <ButtonLink
                    onClick={trackLinkClick}
                    type={linkType as ButtonLinkType}
                    text={linkText}
                    url={link}
                    linkInNewWindow={linkInNewWindow}
                    className="mt-2 lg:mt-8"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </m.div>
      {currentVideoPlaying ? (
        <Modal>
          <div className="flex justify-center items-center w-full h-full">
            <button
              aria-label="Close window"
              className="absolute top-6 right-6 flex items-center justify-center w-10 h-10 hover:bg-black/10 opacity-60 hover:opacity-100 rounded-full transition-all z-10"
              onClick={() => setCurrentVideoPlaying(null)}
            >
              <CloseIcon className="w-6 h-6 text-white" />
            </button>
            <VideoClip controls autoplay {...currentVideoPlaying} />
          </div>
        </Modal>
      ) : null}
    </section>
  );
};

export default TextAndMedia;
